import { RotateCcw } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Field, Steps, NextStep, Callout, RoleBadge, pathFor,
  Diagram, MockField, MockButton,
} from "../shared";

export default function FormsReturnSection() {
  return (
    <div data-testid="guide-forms-return">
      <PageHeading
        icon={RotateCcw}
        title="Return for Amendments"
        description="Send a task back to the person who did the work, with a note explaining what needs fixing. Used by reviewers when a task isn't ready to move forward."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="admin" />
        <span className="text-xs text-white/40">Available from any review or later stage. From the MD Meetings queue, use the per-row Return button.</span>
      </div>

      <Callout variant="info" title="In plain words">
        "Return" means <span className="text-white">"not quite — please fix this"</span>. The task drops back to In Progress and goes back to the original person, along with your reason.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-sm text-white/65 space-y-1.5 ml-2">
          <li>When you're reviewing a task and find typos, missing analysis, or wrong assumptions.</li>
          <li>When the task is basically right but needs another pass before you approve it.</li>
          <li>Don't use it to stop a task for good — use Cancel for that (see the warning below).</li>
        </ul>
      </Section>

      <Section title="What the form looks like">
        <Diagram
          frameLabel="Action Item Detail → Return for Amendments"
          legend={[
            <>Write a clear <span className="text-white">reason</span> — exactly what needs to change.</>,
            <>Click <span className="text-white">Return for Amendments</span> to send it back.</>,
          ]}
        >
          <MockField
            label="Reason for return"
            placeholder="Tables in section 2 don't reconcile to source data — please re-run. Also missing the FX scenario."
            marker={1}
          />
          <MockButton label="Return for Amendments" marker={2} variant="danger" />
        </Diagram>
      </Section>

      <Section title="Step-by-step">
        <Steps
          steps={[
            <>Open the item's detail page and write a clear <span className="text-white">reason</span> in the box.</>,
            <>Click the amber <span className="text-white">"Return for Amendments"</span> button. (From the MD Meetings queue, click <span className="text-white">Return</span> on the row instead.)</>,
          ]}
        />
      </Section>

      <Section title="The one box explained">
        <Field name="Reason for return" required="required">
          <p>A specific note explaining what needs to change. The original assignee will see this — it should give them everything they need to fix the task.</p>
          <p className="text-white/40">Good: "Tables in section 2 don't reconcile to source data — please re-run from source. Also missing the FX scenario."</p>
          <p className="text-white/40">Bad: "Needs more work."</p>
        </Field>
      </Section>

      <NextStep>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Status moves back to <span className="text-amber-300">In Progress</span>.</li>
          <li>"Currently With" reverts to the original assignee.</li>
          <li>Event <span className="text-white">RETURN_FOR_AMENDMENTS</span> records who returned it and the reason.</li>
          <li>The assignee gets an email with the reason, which also appears as an <span className="text-orange-300">orange banner</span> on the Updates page so they know what to fix at a glance.</li>
          <li>When they re-submit, the task moves through the review stages again.</li>
        </ul>
      </NextStep>

      <Callout variant="warning">
        Returning is not the same as cancelling. The task keeps going — it just steps back one stage. Use <Link href={pathFor("lifecycle")} className="text-cyan-300 hover:underline">Cancel</Link> for tasks that should not progress at all.
      </Callout>
    </div>
  );
}
